import React, { useState, useEffect, createContext, useContext, Component } from 'react';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Error boundary to prevent blank screens
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
          <h2 style={{ marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#666', marginBottom: 16 }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
            style={{ background: '#1f2a4d', color: 'white', border: 'none', padding: '8px 20px', borderRadius: 8, cursor: 'pointer' }}>
            Return to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import LoginPage from './pages/LoginPage';
import AppShell from './components/AppShell';
import ClientsPage from './pages/ClientsPage';
import DashboardPage from './pages/DashboardPage';
import ReportBuilderPage from './pages/ReportBuilderPage';
import ReportsPage from './pages/ReportsPage';
import SharedReportPage from './pages/SharedReportPage';
import UsersPage from './pages/UsersPage';
import ClientDetailPage from './pages/ClientDetailPage';
import UTMDefaultsPage from './pages/UTMDefaultsPage';
import EngagementPage from './pages/EngagementPage';
import EngagementTestPage from './pages/EngagementTestPage';
import ClientPortalPage from './pages/ClientPortalPage';
import SentimentPage from './pages/SentimentPage';
import ArchivePage from './pages/ArchivePage';
import MetricoolPage from './pages/MetricoolPage';
import SourcesPage from './pages/SourcesPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Auth context
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// API helper
export const api = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  let res;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, { ...options, headers });
  } catch (networkErr) {
    throw new Error('Network error — please check your connection and try again');
  }
  
  if (res.status === 401 && !path.includes('/auth/login')) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    return;
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `Request failed (${res.status})` }));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  
  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/pdf')) {
    return res.blob();
  }
  return res.json();
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const u = JSON.parse(savedUser);
      if (u.role === 'client_viewer' || u.role === 'client_admin') return 'dashboard';
    }
    return window.location.pathname === '/archive' ? 'archive' : window.location.pathname === '/metricool' ? 'metricool' : window.location.pathname === '/sources' ? 'sources' : 'dashboard';
  });
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // Check for share link in URL
  const isSharedView = window.location.pathname.startsWith('/shared/');
  const shareToken = isSharedView ? window.location.pathname.split('/shared/')[1] : null;

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setCurrentPage('clients');
  };

  const navigate = (page, params = {}) => {
    if (['archive', 'metricool', 'sources'].includes(page) || ['/archive', '/metricool', '/sources'].includes(window.location.pathname)) window.history.replaceState(null, '', ['archive', 'metricool', 'sources'].includes(page) ? `/${page}` : '/');
    if (params.clientId !== undefined) setSelectedClientId(params.clientId);
    if (params.reportId !== undefined) setSelectedReportId(params.reportId);
    setCurrentPage(page);
  };

  if (isSharedView && shareToken) {
    return (
      <>
        <SharedReportPage token={shareToken} />
        <Toaster position="top-right" />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[hsl(var(--background))]">
        <div className="animate-pulse text-lg font-medium text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-right" />
      </>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'clients':
        return <ClientsPage navigate={navigate} />;
      case 'dashboard':
        if (user?.role === 'client_viewer' || user?.role === 'client_admin') {
          return <ClientPortalPage navigate={navigate} />;
        }
        if (selectedClientId) {
          return <DashboardPage clientId={selectedClientId} navigate={navigate} />;
        }
        return <ClientsPage navigate={navigate} />;
      case 'report-builder':
        return <ReportBuilderPage reportId={selectedReportId} clientId={selectedClientId} navigate={navigate} />;
      case 'reports':
        return <ReportsPage clientId={selectedClientId} navigate={navigate} />;
      case 'users':
        return <UsersPage />;
      case 'utm-defaults':
        return <UTMDefaultsPage />;
      case 'engagement':
        return <EngagementPage />;
      case 'engagement-test':
        return <EngagementTestPage />;
      case 'sentiment':
        return <SentimentPage />;
      case 'archive':
        return <ArchivePage />;
      case 'metricool':
        return <MetricoolPage clientId={selectedClientId} navigate={navigate} />;
      case 'sources':
        return <SourcesPage navigate={navigate} />;
      case 'client-detail':
        return <ClientDetailPage clientId={selectedClientId} navigate={navigate} />;
      default:
        // Client users go to client portal
        if (user?.role === 'client_viewer' || user?.role === 'client_admin') {
          return <ClientPortalPage navigate={navigate} />;
        }
        return <ClientsPage navigate={navigate} />;
    }
  };

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, handleLogout, navigate }}>
        <AppShell currentPage={currentPage} navigate={navigate}>
          {renderPage()}
        </AppShell>
        <Toaster position="top-right" />
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
